/*
 * Lizenzmodell-Verwaltung — /settings/licenses.
 *
 * Drei Blöcke auf einer Seite, weil sie zusammen eine Entscheidung tragen:
 *   1. Modelle: CRUD auf den Lizenzmodellen (§4.1). Master-Detail wie im
 *      Client-Katalog, links die Liste, rechts das Formular.
 *   2. Zuweisung: welche Anlage hängt an welchem Modell — mit Reglerstand,
 *      damit man vor dem Wechsel sieht, ob er durchgeht.
 *   3. Warnliste: Anlagen, die ihre Grenzen überschreiten. Eine
 *      Limitsenkung bereinigt nie automatisch; hier steht, was übrig ist.
 *
 * Rechteprüfung: alles hinter `license.admin`. Bewusst getrennt von
 * `plants.admin` — wer eine Anlage administrieren darf, darf damit nicht
 * auch ihre Obergrenzen anheben. Ohne das Recht zeigt die Seite einen
 * Hinweis statt eines halb bedienbaren Formulars.
 *
 * Vorbild für Struktur und Rechteprüfung: ClientCatalogPage.tsx.
 */

import { useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';
import { showConfirm } from '../components/Confirmer';
import { sessionSignal } from '../auth/session';
import { LicenseReportingSection } from './LicenseReportingSection';
import { IconSave, IconCancel, IconCreate, IconDeleteHard } from '../icons/HvacIcons';
import type {
  AssignPlantLicenseResponse,
  LicenseModelDto,
  ListLicenseModelsResponse,
  ListPlantLicensesResponse,
  PlantLicenseDto,
  UpdateLicenseModelResponse,
} from '../api/types';
import './LicensePage.css';

const NEW_MODEL = '__new__';

/** Zahlenformat mit Tausendertrennung — `maxBackfillRows` geht in die
 *  Millionen und ist ohne Trennzeichen nicht lesbar. */
const NUM = new Intl.NumberFormat('de-AT');

export function LicensePage() {
  const perms = sessionSignal.value.user?.permissions ?? [];
  const canAdmin = perms.includes('license.admin');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshAll = () => setRefreshTick((n) => n + 1);

  // includeInactive nur wenn erlaubt — der Server setzt einen Aufrufer ohne
  // Recht sonst still auf die aktive Liste zurück, und die UI würde
  // behaupten, es gäbe keine deaktivierten Modelle.
  const modelsState = useAsync(
    (signal) =>
      api.get<ListLicenseModelsResponse>(
        `/api/licensing/models${canAdmin ? '?includeInactive=true' : ''}`,
        signal,
      ),
    [canAdmin, refreshTick],
  );

  const plantsState = useAsync(
    (signal) =>
      canAdmin
        ? api.get<ListPlantLicensesResponse>('/api/licensing/plants', signal)
        : Promise.resolve<ListPlantLicensesResponse>({ items: [], overrunCount: 0 }),
    [canAdmin, refreshTick],
  );

  const models: LicenseModelDto[] = modelsState.status === 'ok' ? modelsState.data.items : [];
  const plants: PlantLicenseDto[] = plantsState.status === 'ok' ? plantsState.data.items : [];
  const overruns = useMemo(
    () => plants.filter((p) => p.controllerExcess > 0 || p.datapointOverrunControllers > 0),
    [plants],
  );

  const actions = canAdmin ? (
    <button
      type="button"
      class="hv-btn hv-btn--primary hv-btn--sm"
      onClick={() => setSelectedId(NEW_MODEL)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <IconCreate size={14} />
        Neues Modell
      </span>
    </button>
  ) : undefined;

  if (!canAdmin) {
    // Die Verwaltung braucht `license.admin` — die Reporting-Ansicht nicht.
    // §8.2 („kein verstecktes Telemetriemeldeverhalten") wäre sonst eine
    // Formalie: wer nicht sehen darf, was über die eigene Installation
    // gemeldet wird, dem ist es versteckt.
    return (
      <AppShell title="Lizenzmodelle">
        <div class="hv-license">
          <div class="hv-license__empty">
            Für die Lizenzverwaltung fehlt das Recht <code>license.admin</code>. Lizenzmodelle
            legen die Obergrenzen des Betriebs fest und sind deshalb ein eigenes Recht, nicht
            Teil der Anlagenadministration. Was dieser Server monatlich meldet, steht
            unten — das darf jeder sehen, der sich hier anmelden kann.
          </div>
          <LicenseReportingSection canAdmin={false} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Lizenzmodelle" actions={actions}>
      <div class="hv-license">
        <section class="hv-license__models">
          <aside class="hv-license__master">
            <div class="hv-license__master-head">Modelle</div>
            <div class="hv-license__master-list">
              {modelsState.status === 'loading' && (
                <div class="hv-license__empty">Lade Modelle…</div>
              )}
              {modelsState.status === 'error' && (
                <div class="hv-license__empty">Konnte Lizenzmodelle nicht laden.</div>
              )}
              {modelsState.status === 'ok' && models.length === 0 && (
                <div class="hv-license__empty">
                  Kein Lizenzmodell vorhanden. Ohne Modell kann keine Anlage angelegt werden.
                </div>
              )}
              {models.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  class={
                    'hv-license__list-item' +
                    (selectedId === m.id ? ' hv-license__list-item--active' : '')
                  }
                  onClick={() => setSelectedId(m.id)}
                >
                  <span class="hv-license__list-body">
                    <span class="hv-license__list-name">{m.name}</span>
                    <span class="hv-license__list-meta">
                      {m.key} · {m.maxControllersPerPlant} Regler ·{' '}
                      {NUM.format(m.maxRecordedDatapointsPerController)} DPs/Regler
                    </span>
                  </span>
                  <span class="hv-license__badges">
                    {!m.isActive && (
                      <span class="hv-license__badge hv-license__badge--inactive">
                        nicht zuweisbar
                      </span>
                    )}
                    <span class="hv-license__badge">
                      {m.plantCount} {m.plantCount === 1 ? 'Anlage' : 'Anlagen'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <div class="hv-license__detail">
            {selectedId === null ? (
              <div class="hv-license__empty">
                Wähle links ein Modell oder lege ein neues an.
              </div>
            ) : (
              <ModelDetail
                key={selectedId}
                model={selectedId === NEW_MODEL ? null : models.find((m) => m.id === selectedId) ?? null}
                onSaved={(id) => {
                  setSelectedId(id);
                  refreshAll();
                }}
                onDeleted={() => {
                  setSelectedId(null);
                  refreshAll();
                }}
                onClose={() => setSelectedId(null)}
              />
            )}
          </div>
        </section>

        <OverrunPanel rows={overruns} loading={plantsState.status === 'loading'} />

        <AssignmentPanel
          plants={plants}
          models={models}
          loading={plantsState.status === 'loading'}
          onAssigned={refreshAll}
        />

        {/* Vierter Block: was von dieser Installation monatlich nach oben
            gemeldet wird, plus Zustellstand (§8, §10). Steht auf dieser Seite
            und nicht auf einer eigenen, weil es dieselbe Entscheidung trägt
            wie die drei Blöcke darüber — welche Anlage hängt an welchem
            Modell, und was folgt daraus. */}
        <LicenseReportingSection canAdmin={canAdmin} />
      </div>
    </AppShell>
  );
}

// -----------------------------------------------------------------------------
// Modell-Formular
// -----------------------------------------------------------------------------

interface ModelForm {
  key: string;
  name: string;
  maxControllersPerPlant: number | null;
  maxRecordedDatapointsPerController: number | null;
  maxLiveSessionMinutes: number | null;
  maxBackfillRows: number | null;
  isActive: boolean;
}

function toForm(model: LicenseModelDto | null): ModelForm {
  if (model === null) {
    // Vorbelegung mit den Zahlen der Projektanlage — der häufigste
    // Ausgangspunkt für ein neues Modell.
    return {
      key: '',
      name: '',
      maxControllersPerPlant: 5,
      maxRecordedDatapointsPerController: 200,
      maxLiveSessionMinutes: 60,
      maxBackfillRows: 1000000,
      isActive: true,
    };
  }
  return {
    key: model.key,
    name: model.name,
    maxControllersPerPlant: model.maxControllersPerPlant,
    maxRecordedDatapointsPerController: model.maxRecordedDatapointsPerController,
    maxLiveSessionMinutes: model.maxLiveSessionMinutes,
    maxBackfillRows: model.maxBackfillRows,
    isActive: model.isActive,
  };
}

function ModelDetail({
  model,
  onSaved,
  onDeleted,
  onClose,
}: {
  model: LicenseModelDto | null;
  onSaved(id: string): void;
  onDeleted(): void;
  onClose(): void;
}) {
  const isNew = model === null;
  const [form, setForm] = useState<ModelForm>(() => toForm(model));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = <K extends keyof ModelForm>(field: K, value: ModelForm[K]) =>
    setForm((f) => ({ ...f, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const res = await api.post<LicenseModelDto>('/api/licensing/models', {
          key: form.key.trim().toLowerCase(),
          name: form.name.trim(),
          maxControllersPerPlant: form.maxControllersPerPlant,
          maxRecordedDatapointsPerController: form.maxRecordedDatapointsPerController,
          maxLiveSessionMinutes: form.maxLiveSessionMinutes,
          maxBackfillRows: form.maxBackfillRows,
        });
        showToast({
          tone: 'success',
          title: 'Lizenzmodell erstellt',
          message: res.name,
          autoHideAfterMs: 2500,
        });
        onSaved(res.id);
        return;
      }

      const res = await api.patch<UpdateLicenseModelResponse>(
        `/api/licensing/models/${model.id}`,
        {
          name: form.name.trim(),
          maxControllersPerPlant: form.maxControllersPerPlant,
          maxRecordedDatapointsPerController: form.maxRecordedDatapointsPerController,
          maxLiveSessionMinutes: form.maxLiveSessionMinutes,
          maxBackfillRows: form.maxBackfillRows,
          isActive: form.isActive,
        },
      );
      // Eine Limitsenkung wirkt sofort für neue Zuweisungen und neue Regler,
      // bereinigt aber nichts Bestehendes. Wer sie auslöst, soll sofort
      // sehen, welche Anlagen dadurch über ihre Grenze rutschen.
      if (res.newlyOverrunPlants.length > 0) {
        showToast({
          tone: 'warn',
          title: 'Grenzen gesenkt — bestehende Überschreitungen',
          message:
            `Diese Anlagen liegen jetzt über der Reglergrenze und werden NICHT ` +
            `automatisch bereinigt: ${res.newlyOverrunPlants.join(', ')}`,
          autoHideAfterMs: 0,
        });
      } else {
        showToast({
          tone: 'success',
          title: 'Lizenzmodell gespeichert',
          message: res.model.name,
          autoHideAfterMs: 2500,
        });
      }
      onSaved(res.model.id);
    } catch (e) {
      const msg = errorMessage(e, 'Speichern fehlgeschlagen.');
      setError(msg);
      showToast({ tone: 'error', title: 'Nicht gespeichert', message: msg, autoHideAfterMs: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (model === null) return;
    const ok = await showConfirm({
      title: 'Lizenzmodell löschen?',
      message:
        `„${model.name}“ wird endgültig entfernt (kein Soft-Delete). Hängen Anlagen daran, ` +
        `schlägt das Löschen fehl — dann ist „nicht zuweisbar“ der richtige Weg.`,
      confirmLabel: 'Endgültig löschen',
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      await api.delete<void>(`/api/licensing/models/${model.id}`);
      showToast({
        tone: 'success',
        title: 'Lizenzmodell gelöscht',
        message: model.name,
        autoHideAfterMs: 2500,
      });
      onDeleted();
    } catch (e) {
      const msg = errorMessage(e, 'Löschen fehlgeschlagen.');
      setError(msg);
      showToast({
        tone: 'warn',
        title: 'Nicht gelöscht',
        message: msg,
        autoHideAfterMs: 10000,
      });
      setSaving(false);
    }
  };

  return (
    <>
      <div class="hv-license__detail-head">
        <h2 class="hv-license__detail-title">
          {isNew ? 'Neues Lizenzmodell' : model.name}
        </h2>
        {!isNew && (
          <span class="hv-license__badge">
            {model.plantCount} {model.plantCount === 1 ? 'Anlage' : 'Anlagen'}
          </span>
        )}
      </div>

      <div class="hv-license__detail-body">
        <div class="hv-license__grid">
          <TextField
            label="Schlüssel"
            value={form.key}
            onChange={(v) => patch('key', v)}
            // Der Schlüssel ist der stabile Referenzpunkt von Seed und
            // Bestandsmigration — nach dem Anlegen unveränderlich.
            readOnly={!isNew}
            placeholder="z.B. grossprojekt"
          />
          <TextField label="Name" value={form.name} onChange={(v) => patch('name', v)} />
          <NumberField
            label="Regler je Anlage"
            value={form.maxControllersPerPlant}
            onChange={(v) => patch('maxControllersPerPlant', v)}
            hint="Harte Grenze. Wird an jedem Pfad geprüft, der einen Regler entstehen lässt."
          />
          <NumberField
            label="Datenpunkte je Regler"
            value={form.maxRecordedDatapointsPerController}
            onChange={(v) => patch('maxRecordedDatapointsPerController', v)}
            hint="Pro Regler, kein Pool über die Anlage. Durchgesetzt ab Phase 2."
          />
          <NumberField
            label="Live-Modus (Minuten)"
            value={form.maxLiveSessionMinutes}
            onChange={(v) => patch('maxLiveSessionMinutes', v)}
          />
          <NumberField
            label="Nachlieferung (Zeilen)"
            value={form.maxBackfillRows}
            onChange={(v) => patch('maxBackfillRows', v)}
          />
        </div>

        {!isNew && (
          <label class="hv-license__check">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => patch('isActive', (e.currentTarget as HTMLInputElement).checked)}
            />
            <span>
              Zuweisbar
              <span class="hv-license__hint">
                Ausgeschaltet verschwindet das Modell aus allen Zuweisungs-Dropdowns.
                Bestehende Anlagen behalten es und laufen mit ihren Grenzen weiter — das
                ist der Ersatz für Löschen, solange Anlagen daran hängen.
              </span>
            </span>
          </label>
        )}

        {error && <p class="hv-license__error">{error}</p>}
      </div>

      <div class="hv-license__detail-foot">
        <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconCancel size={14} />
            Schließen
          </span>
        </button>
        <div class="hv-license__spacer" />
        {!isNew && (
          <button
            type="button"
            class="hv-btn hv-btn--danger-outline"
            onClick={del}
            disabled={saving || model.plantCount > 0}
            title={
              model.plantCount > 0
                ? 'Nicht löschbar: es hängen Anlagen daran. Stattdessen auf „nicht zuweisbar“ setzen.'
                : 'Endgültig löschen'
            }
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconDeleteHard size={14} />
              Löschen
            </span>
          </button>
        )}
        <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconSave size={14} />
            Speichern
          </span>
        </button>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// Warnliste
// -----------------------------------------------------------------------------

function OverrunPanel({ rows, loading }: { rows: PlantLicenseDto[]; loading: boolean }) {
  return (
    <section class="hv-license__panel">
      <h2 class="hv-license__panel-title">
        Anlagen über ihren Grenzen
        {rows.length > 0 && <span class="hv-license__badge hv-license__badge--warn">{rows.length}</span>}
      </h2>
      <p class="hv-license__panel-note">
        Eine Limitänderung am Modell wirkt sofort für neue Zuweisungen und neue Regler.
        Bestehende Überschreitungen werden <strong>nicht</strong> automatisch bereinigt —
        sie stehen hier, bis jemand entscheidet, was damit passiert.
      </p>
      {loading ? (
        <div class="hv-license__empty">Lade Anlagen…</div>
      ) : rows.length === 0 ? (
        <div class="hv-license__empty">Keine Anlage überschreitet ihre Grenzen.</div>
      ) : (
        <div class="hv-license__table-scroll">
          <table class="hv-license__table">
            <thead>
              <tr>
                <th>Anlage</th>
                <th>Modell</th>
                <th class="hv-license__num">Regler</th>
                <th class="hv-license__num">Grenze</th>
                <th class="hv-license__num">Zu viel</th>
                <th class="hv-license__num">Größter Regler (DPs)</th>
                <th>Befund</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.plantId}>
                  <td>
                    <a href={`/plants/${r.plantId}`}>{r.plantDisplayName}</a>
                  </td>
                  <td>{r.licenseModelName}</td>
                  <td class="hv-license__num">{r.controllers}</td>
                  <td class="hv-license__num">{r.maxControllers}</td>
                  <td class="hv-license__num">
                    {r.controllerExcess > 0 ? (
                      <strong class="hv-license__over">{r.controllerExcess}</strong>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td class="hv-license__num">
                    {NUM.format(r.maxDatapointsOnAnyController)} /{' '}
                    {NUM.format(r.maxRecordedDatapointsPerController)}
                  </td>
                  <td>
                    {r.controllerExcess > 0 && (
                      <div>
                        {r.controllers} Regler vorhanden, Modell erlaubt {r.maxControllers} —{' '}
                        {r.controllerExcess} zu viel.
                      </div>
                    )}
                    {r.datapointOverrunControllers > 0 && (
                      <div class="hv-license__muted">
                        {r.datapointOverrunControllers} Regler über der Datenpunktgrenze. Wird
                        ab Phase 2 durchgesetzt.
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Zuweisung
// -----------------------------------------------------------------------------

function AssignmentPanel({
  plants,
  models,
  loading,
  onAssigned,
}: {
  plants: PlantLicenseDto[];
  models: LicenseModelDto[];
  loading: boolean;
  onAssigned(): void;
}) {
  const [search, setSearch] = useState('');
  const assignable = useMemo(() => models.filter((m) => m.isActive), [models]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return plants;
    return plants.filter(
      (p) =>
        p.plantDisplayName.toLowerCase().includes(needle) ||
        p.licenseModelName.toLowerCase().includes(needle),
    );
  }, [plants, search]);

  return (
    <section class="hv-license__panel">
      <h2 class="hv-license__panel-title">Zuweisung an Anlagen</h2>
      <p class="hv-license__panel-note">
        Ein Wechsel wird abgelehnt, wenn der Bestand der Anlage die Reglergrenze des neuen
        Modells überschreitet — die Meldung nennt dann die Differenz. Eine Überschreitung der
        Datenpunktgrenze blockt nicht, sie wird nur gemeldet.
      </p>
      <div class="hv-license__search">
        <input
          type="search"
          placeholder="Suche in Anlage, Modell…"
          value={search}
          onInput={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      {loading ? (
        <div class="hv-license__empty">Lade Anlagen…</div>
      ) : filtered.length === 0 ? (
        <div class="hv-license__empty">Keine Anlage trifft die Suche.</div>
      ) : (
        <div class="hv-license__table-scroll">
          <table class="hv-license__table">
            <thead>
              <tr>
                <th>Anlage</th>
                <th class="hv-license__num">Regler</th>
                <th>Aktiv betreut</th>
                <th>Zuletzt zugewiesen</th>
                <th>Modell</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <AssignmentRow
                  key={p.plantId}
                  plant={p}
                  models={assignable}
                  onAssigned={onAssigned}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AssignmentRow({
  plant,
  models,
  onAssigned,
}: {
  plant: PlantLicenseDto;
  models: LicenseModelDto[];
  onAssigned(): void;
}) {
  const [busy, setBusy] = useState(false);
  // Ein deaktiviertes Modell steht nicht in `models`, hängt aber an der
  // Anlage. Ohne diese Ergänzung würde das <select> auf den ersten Eintrag
  // springen und eine Zuweisung anzeigen, die es nicht gibt.
  const options = useMemo(() => {
    if (models.some((m) => m.id === plant.licenseModelId)) return models;
    return [
      {
        id: plant.licenseModelId,
        name: `${plant.licenseModelName} (nicht mehr zuweisbar)`,
        maxControllersPerPlant: plant.maxControllers,
      },
      ...models,
    ];
  }, [models, plant]);

  const assign = async (nextId: string) => {
    if (nextId === plant.licenseModelId) return;
    setBusy(true);
    try {
      const res = await api.put<AssignPlantLicenseResponse>(
        `/api/licensing/plants/${plant.plantId}/license`,
        { licenseModelId: nextId },
      );
      showToast({
        tone: res.warning ? 'warn' : 'success',
        title: 'Modell zugewiesen',
        message: res.warning ?? `${plant.plantDisplayName} → ${res.plant.licenseModelName}`,
        autoHideAfterMs: res.warning ? 10000 : 2500,
      });
      onAssigned();
    } catch (e) {
      showToast({
        tone: 'error',
        title: 'Zuweisung abgelehnt',
        message: errorMessage(e, 'Zuweisung fehlgeschlagen.'),
        // Sticky: die Meldung nennt die Differenz und ist der Grund, warum
        // der Operator jetzt Regler abbauen muss. Die darf nicht wegblinken.
        autoHideAfterMs: 0,
      });
      onAssigned();
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        <a href={`/plants/${plant.plantId}`}>{plant.plantDisplayName}</a>
      </td>
      <td class="hv-license__num">
        {plant.controllerExcess > 0 ? (
          <strong class="hv-license__over">
            {plant.controllers} / {plant.maxControllers}
          </strong>
        ) : (
          <>
            {plant.controllers} / {plant.maxControllers}
          </>
        )}
      </td>
      <td>
        {plant.isActivelyManaged ? (
          <span class="hv-license__badge hv-license__badge--ok">ja</span>
        ) : (
          <span class="hv-license__badge hv-license__badge--muted">nein</span>
        )}
      </td>
      <td class="hv-license__muted">{formatDate(plant.licenseAssignedAt)}</td>
      <td>
        <select
          value={plant.licenseModelId}
          disabled={busy}
          onChange={(e) => void assign((e.currentTarget as HTMLSelectElement).value)}
        >
          {options.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} ({m.maxControllersPerPlant} Regler)
            </option>
          ))}
        </select>
      </td>
    </tr>
  );
}

// -----------------------------------------------------------------------------
// Feld-Primitiven + Helfer
// -----------------------------------------------------------------------------

function TextField({
  label,
  value,
  onChange,
  readOnly,
  placeholder,
}: {
  label: string;
  value: string;
  onChange(v: string): void;
  readOnly?: boolean;
  placeholder?: string;
}) {
  return (
    <div class="hv-license__field">
      <label>{label}</label>
      <input
        type="text"
        value={value}
        readOnly={readOnly}
        disabled={readOnly}
        placeholder={placeholder}
        onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: number | null;
  onChange(v: number | null): void;
  hint?: string;
}) {
  return (
    <div class="hv-license__field">
      <label>{label}</label>
      <input
        type="number"
        min={1}
        value={value === null ? '' : String(value)}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value.trim();
          if (raw === '') {
            onChange(null);
            return;
          }
          const n = Number(raw);
          onChange(Number.isFinite(n) ? Math.trunc(n) : null);
        }}
      />
      {hint && <span class="hv-license__hint">{hint}</span>}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-AT', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; detail?: string; title?: string } | undefined;
    const serverMsg = body?.error ?? body?.detail ?? body?.title;
    // Die Server-Meldung ist die eigentliche Information (sie nennt die
    // Differenz) — der Status-Code ist nur Kontext und kommt hinten dran.
    return serverMsg ?? `HTTP ${err.status} — ${fallback}`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
